// ABOUTME: Workspace switcher dropdown component for multi-workspace users.
// ABOUTME: Conditionally renders only if user has access to multiple workspaces.

import { useState, useRef, useEffect } from 'react';
import { Building2, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { useApp } from '@/context/AppContext';
import { AnimatePresence, motion } from 'framer-motion';

export function WorkspaceSwitcher() {
  const {
    currentWorkspaceId,
    availableWorkspaces,
    switchWorkspace,
    isLoadingWorkspaces,
  } = useApp();

  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Don't render if user has only one workspace (must be after all hooks)
  if (availableWorkspaces.length <= 1) {
    return null;
  }

  // Get current workspace details
  const currentWorkspace = availableWorkspaces.find(
    (w) => w.workspace_id === currentWorkspaceId
  );

  const currentName = currentWorkspace?.workspace_name || 'Workspace';

  const handleWorkspaceClick = async (e: React.MouseEvent, workspaceId: string) => {
    e.preventDefault();
    e.stopPropagation();

    if (workspaceId === currentWorkspaceId) {
      setIsOpen(false);
      return;
    }

    setIsSwitching(true);
    setIsOpen(false);
    await switchWorkspace(workspaceId);
    // Note: switchWorkspace does window.location.href = '/' so page will reload
  };

  // Truncate long workspace names
  const truncateName = (name: string, maxLength: number = 15) => {
    return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Switcher Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        disabled={isLoadingWorkspaces || isSwitching}
        className="flex items-center gap-2 px-3 py-1.5 rounded-sm bg-surface-100 hover:bg-surface-200 border border-surface-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
        aria-label="Switch workspace"
      >
        <Building2 className={`w-4 h-4 text-surface-500 ${isSwitching ? 'animate-pulse' : ''}`} />
        <span className="text-sm text-surface-800 max-w-[100px] truncate">
          {isSwitching ? 'Switching...' : truncateName(currentName)}
        </span>
        {isOpen ? (
          <ChevronUp className="w-3 h-3 text-surface-400" />
        ) : (
          <ChevronDown className="w-3 h-3 text-surface-400" />
        )}
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute top-full left-0 mt-2 w-64 bg-surface-0 border border-surface-200 rounded-sm shadow-xl overflow-hidden z-[100]"
          >
            <div className="p-2 max-h-[400px] overflow-y-auto">
              {availableWorkspaces.map((workspace) => {
                const isSelected = workspace.workspace_id === currentWorkspaceId;

                return (
                  <button
                    key={workspace.workspace_id}
                    onClick={(e) => handleWorkspaceClick(e, workspace.workspace_id)}
                    disabled={isSwitching}
                    className="w-full flex items-center justify-between p-3 rounded-sm hover:bg-surface-100 transition-colors text-left group disabled:opacity-50 cursor-pointer"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-surface-800 truncate">
                        {workspace.workspace_name}
                      </div>
                      <div className="text-xs text-surface-400 mt-0.5">
                        <span
                          className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            workspace.role === 'workspace-admin'
                              ? 'bg-primary-50 text-primary-600'
                              : 'bg-surface-100 text-surface-500'
                          }`}
                        >
                          {workspace.role === 'workspace-admin' ? 'Admin' : 'Member'}
                        </span>
                      </div>
                    </div>

                    {isSelected && (
                      <Check className="w-4 h-4 text-primary-600 flex-shrink-0 ml-2" />
                    )}
                  </button>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
